import { useState, useEffect } from "react";
import { useIsMobile } from "./use-mobile";

/**
 * Hook to calculate zoom level for responsive design
 * Returns a zoom factor that scales content down on smaller screens
 * For mobile: scales based on mobile design width (750px) to fit content
 * For desktop: scales between 768-1920px based on window size
 */
export function useResponsiveZoom() {
    const isMobile = useIsMobile();
    const mobileDesignWidth = 768; // Mobile layout designed for ~768px
    
    const calculateInitialZoom = () => {
        if (typeof window === 'undefined') return 1;
        
        const windowWidth = window.innerWidth;

        // Mobile devices - calculate zoom based on mobile design width
        if (windowWidth < 768) {
            return windowWidth / mobileDesignWidth;
        }
        
        if (windowWidth >= 1920) {
            return 1;
        }
        
        const designWidth = 1920;
        const designHeight = 1080;
        const windowHeight = window.innerHeight;
        
        const widthZoom = windowWidth / designWidth;
        const heightZoom = windowHeight / designHeight;
        const zoomDifference = Math.abs(heightZoom - widthZoom) / widthZoom;
        
        if (zoomDifference <= 0.15 && heightZoom < widthZoom) {
            return heightZoom;
        } else {
            return widthZoom;
        }
    };
    
    const [zoomLevel, setZoomLevel] = useState(calculateInitialZoom);

    useEffect(() => {
        const calculateZoom = () => {
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            
            // Mobile devices - calculate zoom based on mobile design width
            if (isMobile) {
                setZoomLevel(windowWidth / mobileDesignWidth);
                return;
            }
            
            const designWidth = 1920;
            const designHeight = 1080;
            
            if (windowWidth >= designWidth) {
                setZoomLevel(1);
            } else if (windowWidth >= 768) {
                const widthZoom = windowWidth / designWidth;
                const heightZoom = windowHeight / designHeight;
                const zoomDifference = Math.abs(heightZoom - widthZoom) / widthZoom;
                
                if (zoomDifference <= 0.15 && heightZoom < widthZoom) {
                    setZoomLevel(heightZoom);
                } else {
                    setZoomLevel(widthZoom);
                }
            }
        };

        calculateZoom();
        window.addEventListener('resize', calculateZoom);
        return () => window.removeEventListener('resize', calculateZoom);
    }, [isMobile]);

    return zoomLevel;
}
